import React, { useState, useCallback } from 'react';
import Dialog from '@mui/material/Dialog';
import DialogTitle from '@mui/material/DialogTitle';
import DialogContent from '@mui/material/DialogContent';
import IconButton from '@mui/material/IconButton';
import { LocalizationProvider } from '@mui/x-date-pickers';
import { AdapterDayjs } from '@mui/x-date-pickers/AdapterDayjs';
import LoadingButton from '@mui/lab/LoadingButton';
import Typography from '@mui/material/Typography';
import { X } from 'lucide-react';
import { useNotifications } from 'src/notifications';

import type { IProperty } from 'src/services/api';
import Select from 'src/components/input/select/Select';
import { usePropertyForm } from 'src/components/create-or-update-property-modal/use-property-form';
import PropertyForm from 'src/components/create-or-update-property-modal/PropertyForm';
import PropertyImageEditor from 'src/components/create-or-update-property-modal/PropertyImageEditor';
import PropertyFactsSection from 'src/components/create-or-update-property-modal/PropertyFactsSection';
import PropertyTourSettingsSection from 'src/components/create-or-update-property-modal/PropertyTourSettingsSection';
import PropertyApplicationLinksSection from 'src/components/create-or-update-property-modal/PropertyApplicationLinksSection';
import {
    type PropertySectionKey,
    usePropertySectionNavigation,
} from 'src/components/create-or-update-property-modal/use-property-section-navigation';

import styles from 'src/components/create-or-update-property-modal/CreateOrUpdatePropertyModal.module.css';

type Props = {
    title?: string;
    property?: IProperty;
    defaultValues?: Partial<IProperty>;
    opened: boolean;
    onClose: () => void;
    onSave?: (property: IProperty) => Promise<void> | void;
    initialSection?: PropertySectionKey;
};

const SECTION_LABELS: Record<PropertySectionKey, string> = {
    basicInfo: 'Basic Info',
    propertyFacts: 'Property Facts',
    tourSettings: 'Tour Settings',
    applicationLinks: 'Application Settings',
};

export default function CreateOrUpdatePropertyModal({
    title: propsTitle,
    property,
    defaultValues,
    opened,
    onClose,
    onSave,
    initialSection = 'basicInfo',
}: Props) {

    const [imageFile, setImageFile] = useState<File | null>(null);
    const { addNotification } = useNotifications();

    const onSuccess = (_property: IProperty) => {
        setImageFile(null);
        onSave?.(_property);
        if (!property) {
            onClose();
        }
    };

    const { form, submit } = usePropertyForm({
        onSuccess,
        property,
        defaultValues,
        imageFile,
    });
    const isEditing = !!property;
    const title = propsTitle || (isEditing ? 'Edit Property' : 'Create Property');

    const visibleSections: PropertySectionKey[] = ['basicInfo'];
    if (property?.facts !== undefined) {
        visibleSections.push('propertyFacts');
    }
    if (property) {
        visibleSections.push('tourSettings', 'applicationLinks');
    }

    const {
        activeSection,
        contentRef,
        handleContentScroll,
        scrollToSection,
        setSectionRef,
    } = usePropertySectionNavigation(visibleSections, initialSection);
    const handleSave = useCallback(
        (event?: React.BaseSyntheticEvent) => form.handleSubmit(
            submit,
            () => addNotification('Fix the highlighted property fields before saving.', 'error')
        )(event),
        [addNotification, form, submit]
    );

    return (
        <LocalizationProvider dateAdapter={AdapterDayjs}>
            <Dialog
                open={opened}
                onClose={onClose}
                PaperProps={{
                    className: styles.paper,
                    sx: {
                        width: '100%',
                        maxWidth: 'md',
                        minHeight: '80%',
                    }
                }}
            >
                <DialogTitle className={styles.dialogTitle}>
                    <Typography component="span" variant="h6">{title}</Typography>
                    <IconButton aria-label="Close property editor" onClick={onClose} data-testid="close-property-editor-button">
                        <X size={20} />
                    </IconButton>
                </DialogTitle>
                <div className={styles.header}>
                    <PropertyImageEditor
                        form={form}
                        currentImageUrl={property?.image_url}
                        selectedFile={imageFile}
                        onSelectFile={setImageFile}
                    />
                </div>
                {visibleSections.length > 1 && (
                    <div className={styles.mobileNav}>
                        <Select
                            label="Section"
                            options={visibleSections.map((key) => ({
                                value: key,
                                label: <Typography component="span" variant="body2">{SECTION_LABELS[key]}</Typography>,
                            }))}
                            value={activeSection}
                            onChange={scrollToSection}
                            size="small"
                            fullWidth
                            data-testid="property-section-mobile-select"
                        />
                    </div>
                )}
                <div className={styles.body}>
                    {visibleSections.length > 1 && (
                        <nav className={styles.sidebar} aria-label="Property sections">
                            {visibleSections.map((key) => (
                                <button
                                    key={key}
                                    type="button"
                                    className={key === activeSection ? `${styles.navItem} ${styles.navItemActive}` : styles.navItem}
                                    onClick={() => scrollToSection(key)}
                                    data-testid={`property-section-nav-${key}`}
                                >
                                    <Typography component="span" variant="body2">
                                        {SECTION_LABELS[key]}
                                    </Typography>
                                </button>
                            ))}
                        </nav>
                    )}
                    <DialogContent
                        ref={contentRef}
                        className={styles.content}
                        onScroll={handleContentScroll}
                    >
                        <section
                            ref={setSectionRef('basicInfo')}
                            className={styles.section}
                        >
                            <Typography fontWeight={500} className={styles.basicInfoTitle} data-property-section-title>Basic Info</Typography>
                            <PropertyForm
                                form={form}
                                onSubmit={handleSave}
                            />
                            <div className={styles.basicInfoActions}>
                                <LoadingButton className={styles.sectionSaveButton} variant="contained" size="small" loading={form.formState.isSubmitting} disabled={!form.formState.isDirty && !imageFile} onClick={handleSave} data-testid="save-basic-info-button">
                                    Save
                                </LoadingButton>
                            </div>
                        </section>

                        {property?.facts !== undefined && (
                            <section
                                ref={setSectionRef('propertyFacts')}
                                className={styles.section}
                            >
                                <PropertyFactsSection property={property} />
                            </section>
                        )}

                        {property && (
                            <>
                                <section
                                    ref={setSectionRef('tourSettings')}
                                    className={styles.section}
                                >
                                    <PropertyTourSettingsSection property={property} />
                                </section>

                                <section
                                    ref={setSectionRef('applicationLinks')}
                                    className={styles.section}
                                >
                                    <PropertyApplicationLinksSection property={property} />
                                </section>
                            </>
                        )}
                        {visibleSections.length > 1 && (
                            <div className={styles.sectionScrollSpacer} aria-hidden="true" />
                        )}
                    </DialogContent>
                </div>

            </Dialog>
        </LocalizationProvider>
    );
}
